import {
  Theme,
  border2,
  color,
  focusStyles,
  propertyColorStyles,
  rem,
  reset,
  radius,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';

export const tag = tagName('icon-button');
const theme = new Theme(tag);

const propertyColorCallback = (buttonColor: PropertyColor) => {
  const selector = unsafeCSS(`[color=${buttonColor}]`);

  return css`
    :host(${selector}) :not([disabled]) {
      :where(.button[underlined] label) {
        border-block-end-color: ${color(buttonColor, 600)};
      }

      :where(a-icon) {
        color: ${color(buttonColor, 600)};
      }

      :where(.button:hover a-icon) {
        color: ${color(buttonColor, 700)};
      }

      :where(.button:active a-icon) {
        color: ${color(buttonColor, 800)};
      }
    }

    :host(${selector}[variant='ghost']) .button:not([disabled]) {
      background-color: transparent;

      :where(.button[underlined] label) {
        border-block-end-color: ${token('foreground-secondary')};
      }

      :where(a-icon) {
        color: ${token('foreground-secondary')};
      }

      &:where(:hover, :active) {
        background-color: ${token('background-hover')};
      }

      :where(.button[underlined]:hover label) {
        border-block-end-color: ${token('foreground-interactive-neutral-hover')};
      }

      :where(.button:hover a-icon) {
        color: ${token('foreground-interactive-neutral-hover')};
      }
    }

    :host(${selector}[variant='ghost'][active]) .button {
      background-color: ${token('background-hover')};
    }

    :host(:where([variant='dual'])) :where(.button) {
      background-color: ${token('icon-button-dual-background-default')};
      ${theme.props([
        ['box-shadow', 'box-shadow', token('elevation-shadow-raised')],
        ['border-radius', 'border-radius', rem(12)],
      ])};
    }

    .small {
      font-size: ${rem(12)};
    }

    .default {
      font-size: 1em;
    }

    .medium {
      font-size: ${rem(20)};
    }

    .large {
      font-size: ${rem(24)};
    }

    .larger {
      font-size: ${rem(28)};
    }

    .largest {
      font-size: ${rem(32)};
    }
  `;
};

export const styles = [
  reset.button,
  css`
    :host {
      display: inline-flex;
    }

    :where(.button) {
      align-items: center;
      background-color: ${token('background-tertiary')};
      color: ${token('foreground-primary')};
      display: inline-flex;
      place-content: center;
      position: relative;
      text-decoration: none;

      ${theme.props([
        ['min-block-size', 'min-block-size', rem(40)],
        ['min-inline-size', 'min-inline-size', rem(40)],
        ['gap', 'gap', spacing(100)],
        ['padding', 'padding', spacing(100, 150)],
        ['border-radius', 'border-radius', rem(20)],
      ])};
    }

    :where(.button),
    :where(.button) * {
      cursor: pointer;
    }

    :where(a-icon) {
      min-inline-size: ${rem(24)};
    }

    :where(.without-content) {
      padding: 0;
    }

    :where(.button:focus-visible) {
      ${focusStyles};
    }

    :where(.button:hover) {
      background-color: ${token('background-hover')};
    }

    :where(.button[underlined] label) {
      border-block-end: ${border2(1, 'foreground-primary', 'dashed')};
      border-block-start: 1px solid transparent;
    }
  `,
  propertyColorStyles(propertyColorCallback),
  css`
    :where(.button[disabled]) {
      background-color: ${token('background-disabled')};
      color: ${token('foreground-disabled')};
      cursor: default;
      pointer-events: none;

      &[underlined] label {
        border-block-end: ${border2(1, 'foreground-disabled', 'dashed')};
      }

      label {
        color: ${token('foreground-disabled')};
      }
    }

    :where(.indicator) {
      background: ${token('icon-button-indicator-background-default')};
      block-size: ${rem(10)};
      border-radius: ${radius('extra-large')};
      inline-size: ${rem(10)};
      inset-block-start: ${rem(0)};
      inset-inline-end: ${rem(0)};
      position: absolute;
    }
  `,
];
